import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  vacationService,
  VacationFilters,
  CreateVacationRequestDto,
  ReviewVacationRequestDto,
  CancelVacationRequestDto,
  UpdateVacationBalanceDto,
  CreateVacationPolicyDto,
  UpdateVacationPolicyDto,
} from '../services';
import { AccrualType } from '../types';

/**
 * Query Keys
 * Centralized query key factory for vacation data.
 */
export const vacationKeys = {
  all: ['vacations'] as const,
  requests: () => [...vacationKeys.all, 'requests'] as const,
  requestList: (filters?: VacationFilters) => [...vacationKeys.requests(), 'list', filters] as const,
  myRequests: (memberId: string, page?: number) =>
    [...vacationKeys.requests(), 'mine', memberId, page] as const,
  requestDetail: (id: string) => [...vacationKeys.requests(), 'detail', id] as const,
  pending: (managerId: string, page?: number) =>
    [...vacationKeys.requests(), 'pending', managerId, page] as const,
  balances: () => [...vacationKeys.all, 'balances'] as const,
  balance: (memberId: string, year: number) => [...vacationKeys.balances(), memberId, year] as const,
  usage: () => [...vacationKeys.all, 'usage'] as const,
  usageSummary: (filters?: { year?: number; accrualType?: AccrualType; memberId?: string; page?: number }) =>
    [...vacationKeys.usage(), filters] as const,
  policies: () => [...vacationKeys.all, 'policies'] as const,
};

const VACATIONS_CACHE_TTL_MS = 5 * 60 * 1000;

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

/** Fetch vacation requests (role-scoped server-side) with pagination. */
export const useVacationRequests = (filters?: VacationFilters, enabled = true) => {
  return useQuery({
    queryKey: vacationKeys.requestList(filters),
    queryFn: () => vacationService.getRequests(filters),
    staleTime: VACATIONS_CACHE_TTL_MS,
    // Override the global refetchOnMount:false so an invalidated list (e.g. after
    // creating a request and navigating back to /vacations) refetches on mount.
    refetchOnMount: true,
    enabled,
  });
};

/** Fetch the current member's own vacation requests. */
export const useMyVacationRequests = (memberId: string, page?: number, limit?: number) => {
  return useQuery({
    queryKey: vacationKeys.myRequests(memberId, page),
    queryFn: () => vacationService.getMyRequests(memberId, { page, limit }),
    enabled: !!memberId,
    staleTime: VACATIONS_CACHE_TTL_MS,
    refetchOnMount: true,
  });
};

/** Fetch a single vacation request by ID. */
export const useVacationRequest = (id: string) => {
  return useQuery({
    queryKey: vacationKeys.requestDetail(id),
    queryFn: () => vacationService.getRequestById(id),
    enabled: !!id,
  });
};

/** Fetch a manager's pending-approval queue. */
export const usePendingVacationRequests = (managerId: string, page?: number, limit?: number) => {
  return useQuery({
    queryKey: vacationKeys.pending(managerId, page),
    queryFn: () => vacationService.getPendingForManager(managerId, { page, limit }),
    enabled: !!managerId,
    staleTime: VACATIONS_CACHE_TTL_MS,
    refetchOnMount: true,
  });
};

/** Fetch a member's balance for a given year. */
export const useVacationBalance = (memberId: string, year: number, enabled = true) => {
  return useQuery({
    queryKey: vacationKeys.balance(memberId, year),
    queryFn: () => vacationService.getBalance(memberId, year),
    enabled: enabled && !!memberId && !!year,
    staleTime: VACATIONS_CACHE_TTL_MS,
    // Override the global refetchOnMount:false so the balance tiles refetch
    // when returning to /vacations after a request was created/cancelled
    // (the query is inactive at invalidation time, so it only gets marked stale).
    refetchOnMount: true,
  });
};

/** Fetch the team usage summary (manager/admin reporting). */
export const useTeamUsageSummary = (
  filters?: {
    year?: number;
    accrualType?: AccrualType;
    memberId?: string;
    search?: string;
    page?: number;
    limit?: number;
  },
  enabled = true,
) => {
  return useQuery({
    queryKey: vacationKeys.usageSummary(filters),
    queryFn: () => vacationService.getUsageSummary(filters),
    enabled,
    staleTime: VACATIONS_CACHE_TTL_MS,
  });
};

/** Fetch all per-country vacation policies. */
export const useVacationPolicies = (enabled = true) => {
  return useQuery({
    queryKey: vacationKeys.policies(),
    queryFn: () => vacationService.getPolicies(),
    enabled,
    staleTime: VACATIONS_CACHE_TTL_MS,
  });
};

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

/** Create a new vacation request. */
export const useCreateVacationRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateVacationRequestDto) => vacationService.createRequest(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: vacationKeys.requests() });
      queryClient.invalidateQueries({ queryKey: vacationKeys.balances() });
      queryClient.invalidateQueries({ queryKey: vacationKeys.usage() });
    },
  });
};

/** Cancel a vacation request. */
export const useCancelVacationRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data?: CancelVacationRequestDto }) =>
      vacationService.cancelRequest(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: vacationKeys.requests() });
      queryClient.invalidateQueries({ queryKey: vacationKeys.balances() });
      queryClient.invalidateQueries({ queryKey: vacationKeys.usage() });
    },
  });
};

/** Approve a vacation request (manager/admin). */
export const useApproveVacationRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data?: ReviewVacationRequestDto }) =>
      vacationService.approveRequest(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: vacationKeys.requests() });
      queryClient.invalidateQueries({ queryKey: vacationKeys.balances() });
      queryClient.invalidateQueries({ queryKey: vacationKeys.usage() });
    },
  });
};

/** Reject a vacation request (manager/admin). */
export const useRejectVacationRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data?: ReviewVacationRequestDto }) =>
      vacationService.rejectRequest(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: vacationKeys.requests() });
      queryClient.invalidateQueries({ queryKey: vacationKeys.usage() });
    },
  });
};

/** Admin: create-or-update a member's balance for a given year (upsert). */
export const useUpdateMemberBalance = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ memberId, year, data }: { memberId: string; year: number; data: UpdateVacationBalanceDto }) =>
      vacationService.upsertBalance(memberId, year, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: vacationKeys.balance(variables.memberId, variables.year) });
      queryClient.invalidateQueries({ queryKey: vacationKeys.usage() });
    },
  });
};

/** Admin: create a per-country vacation policy. */
export const useCreateVacationPolicy = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateVacationPolicyDto) => vacationService.createPolicy(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: vacationKeys.policies() });
    },
  });
};

/** Admin: update a per-country vacation policy. */
export const useUpdateVacationPolicy = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateVacationPolicyDto }) =>
      vacationService.updatePolicy(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: vacationKeys.policies() });
    },
  });
};

/** Admin: delete a per-country vacation policy. */
export const useDeleteVacationPolicy = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => vacationService.deletePolicy(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: vacationKeys.policies() });
    },
  });
};
